// IndexedDB queue for voice uploads that failed due to network.
// The service worker reads this same DB to replay uploads in the background.
const DB_NAME = 'ase-crm-voice-queue';
// v2 adds the `meta` store, which mirrors the current auth token so the
// service worker (no localStorage access) can replay with a LIVE token
// instead of the one frozen into the entry at record time. Bumped in
// lockstep with sw.js — opening the same DB at two different versions
// throws VersionError, so these must always move together.
const DB_VERSION = 2;
const STORE = 'pending';
const META_STORE = 'meta';

const TOKEN_KEY = 'utsc_token';

// After this many failed attempts an entry stops being retried automatically
// and is parked as 'failed' — it stays in the DB (the audio is the rep's only
// copy) but no longer jams the automatic flush on every 'online' event. The
// rep can still force a retry or discard/download it from the capture screen.
export const MAX_ATTEMPTS = 5;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
    };
    // An older service worker still holding a v1 connection blocks the v1->v2
    // upgrade. Fail fast instead of hanging forever on a promise that never
    // settles — the SW self-updates on this same page load, so the next call
    // goes through.
    req.onblocked = () => reject(new Error('voice queue upgrade blocked'));
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = () => reject(req.error);
  });
}

function currentToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

// Mirror the live token into IDB for the service worker. Cheap; call it
// wherever the queue is about to matter (mount, retry, sync request).
export async function mirrorAuthToken() {
  const token = currentToken();
  if (!token) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(META_STORE, 'readwrite');
    tx.objectStore(META_STORE).put({ key: 'authToken', value: token });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Store audio blob + metadata for later upload.
// The token captured here is only ever a FALLBACK — the retry paths prefer
// the live token. Replaying a token frozen at record time is what left five
// of a rep's recordings 401-ing forever on every retry (2026-09-01): JWTs
// expire after 7 days, and the queue happily replayed a dead one for good.
export async function enqueue({ audioBlob, mimeType, customerId, customerNameHint, touchType, domain, visibility, durationSec, noteDate, followUpId }) {
  const buf = await audioBlob.arrayBuffer();
  const db = await openDB();
  await mirrorAuthToken().catch(() => {});
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).add({
      audioBuffer: buf,
      mimeType: mimeType || audioBlob.type || 'audio/webm',
      customerId: customerId || null,
      customerNameHint: customerNameHint || null,
      touchType: touchType || 'note',
      domain: domain || null,
      visibility: visibility || 'team',
      durationSec: durationSec || 0,
      // Carried through so a recording that flushes days later is still dated
      // to the visit, not to whenever the upload finally succeeded.
      noteDate: noteDate || null,
      followUpId: followUpId || null,
      authToken: currentToken(),
      queuedAt: new Date().toISOString(),
      attempts: 0,
      state: 'pending',
      lastStatus: null,
      lastError: null,
      lastTriedAt: null,
    });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function listPending() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function remove(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Record the outcome of a failed attempt on the entry itself, so the capture
// screen can tell the rep WHICH failure this is ("sign in again" vs "the
// recording didn't come through") instead of one generic string, and so a
// permanently-broken entry eventually stops being retried.
async function recordFailure(id, { status, message }) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const get = store.get(id);
    get.onsuccess = () => {
      const entry = get.result;
      if (!entry) return;
      entry.attempts = (entry.attempts || 0) + 1;
      entry.lastStatus = status ?? null;
      entry.lastError = message || null;
      entry.lastTriedAt = new Date().toISOString();
      // A network failure is not the entry's fault — it must never burn
      // through the attempt budget, or a rep who spends a day out of
      // coverage would come back to a queue that has given up.
      if (status !== null && entry.attempts >= MAX_ATTEMPTS) entry.state = 'failed';
      store.put(entry);
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function count() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Rebuild the recording as a Blob so the rep can play it back or save it off
// the device when it genuinely can't be uploaded — the queue is the only copy
// of that audio, so "discard" must never be the sole way out.
export function blobFor(entry) {
  return new Blob([entry.audioBuffer], { type: entry.mimeType || 'audio/webm' });
}

export function fileNameFor(entry) {
  const ext = (entry.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
  const stamp = (entry.queuedAt || '').replace(/[:.]/g, '-') || Date.now();
  return `ase-crm-voice-note-${stamp}.${ext}`;
}

function bodyFor(entry) {
  const ext = (entry.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
  const fd = new FormData();
  fd.append('audio', blobFor(entry), `note-offline.${ext}`);
  if (entry.customerId) fd.append('customer_id', String(entry.customerId));
  else if (entry.customerNameHint) fd.append('customer_name_hint', entry.customerNameHint);
  fd.append('duration_sec', String(entry.durationSec || 0));
  fd.append('touch_type', entry.touchType || 'note');
  if (entry.domain) fd.append('domain', entry.domain);
  fd.append('visibility', entry.visibility || 'team');
  if (entry.noteDate) fd.append('note_date', entry.noteDate);
  if (entry.followUpId) fd.append('follow_up_id', String(entry.followUpId));
  return fd;
}

/**
 * Attempt to upload pending entries directly from the main thread.
 * Used as the iOS / non-Background-Sync fallback (triggered on 'online').
 *
 * Entries are attempted independently — one entry failing (stale auth token,
 * a transient network blip) must not stop the rest of the queue from being
 * tried. Previously this looped with a bare `await fetch` per entry and threw
 * on the first failure, which silently jammed every recording queued behind
 * it (found investigating a rep's "recordings queue but never save" report,
 * 2026-07-28).
 *
 * Pass `{ includeFailed: true }` for an explicit rep-initiated retry, so a
 * parked entry gets one more real chance after (say) a fresh sign-in.
 * Returns a per-entry summary instead of throwing a count-only Error, so the
 * caller can show the rep what actually happened.
 */
export async function flushPending(onUploaded, { includeFailed = false } = {}) {
  await mirrorAuthToken().catch(() => {});
  const all = await listPending();
  const pending = all.filter(e => includeFailed || e.state !== 'failed');
  // Prefer the LIVE token; the entry's own token is only a fallback for the
  // case where the rep is signed out entirely.
  const live = currentToken();
  const results = { uploaded: 0, attempted: pending.length, failures: [] };

  for (const entry of pending) {
    const token = live || entry.authToken || '';
    try {
      const res = await fetch('/api/notes/voice', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: bodyFor(entry),
      });
      if (!res.ok) {
        let message = `Upload rejected (${res.status})`;
        try {
          const payload = await res.json();
          if (payload?.error) message = payload.error;
        } catch { /* non-JSON error body */ }
        await recordFailure(entry.id, { status: res.status, message });
        results.failures.push({ id: entry.id, status: res.status, message });
        continue;
      }
      const data = await res.json();
      await remove(entry.id);
      results.uploaded++;
      onUploaded?.(data.data);
    } catch (err) {
      // No HTTP status at all = still offline. Leave it fully retryable.
      await recordFailure(entry.id, { status: null, message: 'No connection' });
      results.failures.push({ id: entry.id, status: null, message: 'No connection' });
    }
  }
  return results;
}

// Register Background Sync (Chrome/Android) or fall back to a SW message.
export async function requestSync() {
  await mirrorAuthToken().catch(() => {});
  if (!navigator.serviceWorker?.ready) return;
  const reg = await navigator.serviceWorker.ready;
  if ('sync' in reg) {
    await reg.sync.register('voice-upload-sync');
  } else {
    reg.active?.postMessage({ type: 'RETRY_VOICE_UPLOADS' });
  }
}
